import fetchWithAuthRetry from '../utils/fetchWithAuthRetry';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.adjusterphoto.com';

// Types
export interface User {
  id: string;
  email: string;
  subscription_active: boolean;
  claims_tier: string;
  admin: boolean;
  created_at: string;
}

export interface UsersListResponse {
  users: User[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface StatsResponse {
  total_users: number;
  active_subscriptions: number;
  admin_users: number;
  claims_tiers: { [key: string]: number };
  recent_registrations: number;
}

export interface AdminActionResponse {
  email?: string;
  message?: string;
  temporary_password?: string;
  email_error?: string;
  old_claims_tier?: string;
  new_claims_tier?: string;
}

export interface UserStatsResponse {
  user_info: {
    email: string;
    admin: boolean;
    subscription_active: boolean;
    claims_tier: string;
    created_at: string;
    user_id: string;
  };
  statistics: {
    total_images: number;
    total_locations: number;
    images_by_status: { [key: string]: number };
    recent_uploads_7d: number;
    total_devices: number;
    last_activity: string | null;
    storage_used_mb: number;
    total_downloads: number;
  };
  recent_activity: Array<{
    type: string;
    description: string;
    timestamp: string;
  }>;
  locations_data: Array<{
    location: string;
    image_count: number;
    last_upload: string;
  }>;
}

export interface UsageStatsResponse {
  total_requests: number;
  requests_today: number;
  total_tokens: number;
  total_cost: number;
  cost_today: number;
  cost_by_service: { [key: string]: number };
  tokens_by_service: { [key: string]: number };
  requests_by_service: { [key: string]: number };
}

export interface UserUsageStatsResponse {
  total_requests: number;
  requests_today: number;
  total_tokens: number;
  total_cost: number;
  cost_today: number;
  cost_by_service: { [key: string]: number };
  tokens_by_service: { [key: string]: number };
  requests_by_service: { [key: string]: number };
  last_request_time: string | null;
}

export interface TierConfig {
  id: string;
  display_name: string;
  location_expiry_days: number | null;
  max_active_locations: number | null;
  max_downloads_per_location: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface TiersResponse {
  tiers: TierConfig[];
}

export interface UpdateTierRequest {
  tier_id: string;
  display_name?: string;
  location_expiry_days: number | null;
  max_active_locations: number | null;
  max_downloads_per_location: number | null;
}

// Get paginated list of users
export async function getUsers(
  page: number = 1,
  pageSize: number = 20,
  search?: string
): Promise<UsersListResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  if (search) {
    params.append('search', search);
  }

  const response = await fetchWithAuthRetry(
    `${API_URL}/api/admin/users?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }

  return response.json();
}

// Get dashboard statistics
export async function getStats(): Promise<StatsResponse> {
  const response = await fetchWithAuthRetry(
    `${API_URL}/api/admin/stats`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch statistics');
  }

  return response.json();
}

// Toggle user subscription status
export async function toggleUserSubscription(email: string): Promise<User> {
  const response = await fetchWithAuthRetry(
    `${API_URL}/admin/update-user-subscription`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_email: email }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to update subscription');
  }

  return response.json();
}

// Update user claims tier
export async function updateUserClaimsTier(email: string): Promise<AdminActionResponse> {
  const response = await fetchWithAuthRetry(
    `${API_URL}/admin/update-claims-tier`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_email: email }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to update claims tier');
  }

  return response.json();
}

// Force password reset for a user
export async function forcePasswordReset(email: string): Promise<AdminActionResponse> {
  const response = await fetchWithAuthRetry(
    `${API_URL}/api/admin/force-reset`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_email: email }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to reset password');
  }

  return response.json();
}

// Get detailed statistics for a specific user
export async function getUserStats(email: string): Promise<UserStatsResponse> {
  const response = await fetchWithAuthRetry(
    `${API_URL}/api/admin/user-stats?email=${encodeURIComponent(email)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch user statistics');
  }

  return response.json();
}

// Get API usage statistics
export async function getUsageStats(): Promise<UsageStatsResponse> {
  const response = await fetchWithAuthRetry(
    `${API_URL}/api/admin/usage-stats`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch usage statistics');
  }

  return response.json();
}

// Get API usage statistics for a specific user
export async function getUserUsageStats(email: string): Promise<UserUsageStatsResponse> {
  const response = await fetchWithAuthRetry(
    `${API_URL}/api/admin/user-usage-stats?email=${encodeURIComponent(email)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch user usage statistics');
  }

  return response.json();
}

// Get all tier configurations
export async function getTiers(): Promise<TiersResponse> {
  const response = await fetchWithAuthRetry(
    `${API_URL}/api/admin/tiers`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch tiers');
  }

  return response.json();
}

// Update a tier configuration
export async function updateTier(request: UpdateTierRequest): Promise<{ message: string; tier: TierConfig }> {
  const response = await fetchWithAuthRetry(
    `${API_URL}/api/admin/tiers`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to update tier');
  }

  return response.json();
}